import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import type { HangarUnitRow, HangarUnitUpdate } from '@/types/hangar-units'
import { HANGAR_UNIT_STATUS_LABELS, HANGAR_UNIT_STATUSES } from '@/types/hangar-units'
import type { PricingModel } from '@/types/hangars'

// Validation schema for unit edit
const unitEditSchema = z.object({
  unit_code: z.string().min(1, 'Code is required').max(10),
  door_width: z.coerce.number().min(20).max(80).nullable().optional(),
  depth: z.coerce.number().min(15).max(60).nullable().optional(),
  tail_width: z.coerce.number().min(10).max(50).nullable().optional(),
  is_leasable: z.boolean(),
  status: z.enum(['available', 'leased', 'maintenance']),
  // Pricing override fields
  pricing_override: z.boolean(),
  unit_monthly_rate: z.coerce.number().min(0).nullable().optional(),
  unit_rate_per_sqft: z.coerce.number().min(0).nullable().optional(),
})

type UnitEditFormValues = z.infer<typeof unitEditSchema>

interface UnitDetailPanelProps {
  /** The unit being edited */
  unit: HangarUnitRow
  /** Template dimensions for reference/defaults */
  template: { door_width: number; depth: number; tail_width: number }
  /** Parent hangar's pricing model */
  parentPricingModel: PricingModel
  /** Parent hangar's monthly rate (for fixed pricing) */
  parentMonthlyRate?: number | null
  /** Parent hangar's rate per sqft (for per_sqft pricing) */
  parentRatePerSqft?: number | null
  /** Called when changes are saved */
  onSave: (id: string, data: HangarUnitUpdate) => void
  /** Called when delete is requested */
  onDelete: (id: string) => void
  /** Called when panel is closed */
  onClose: () => void
  /** Whether save is in progress */
  isLoading?: boolean
}

/**
 * Panel for editing individual unit properties
 * Allows overriding template dimensions, changing status, and custom pricing
 */
export function UnitDetailPanel({
  unit,
  template,
  parentPricingModel,
  parentMonthlyRate,
  parentRatePerSqft,
  onSave,
  onDelete,
  onClose,
  isLoading,
}: UnitDetailPanelProps) {
  const form = useForm<UnitEditFormValues>({
    resolver: zodResolver(unitEditSchema) as any,
    defaultValues: {
      unit_code: unit.unit_code,
      door_width: unit.door_width ?? undefined,
      depth: unit.depth ?? undefined,
      tail_width: unit.tail_width ?? undefined,
      is_leasable: unit.is_leasable,
      status: unit.status,
      pricing_override: unit.pricing_override ?? false,
      unit_monthly_rate: unit.unit_monthly_rate ?? undefined,
      unit_rate_per_sqft: unit.unit_rate_per_sqft ?? undefined,
    },
  })

  // Watch pricing override to conditionally show fields
  const watchedPricingOverride = form.watch('pricing_override')

  const handleSubmit = (data: UnitEditFormValues) => {
    onSave(unit.id, {
      unit_code: data.unit_code,
      door_width: data.door_width ?? null,
      depth: data.depth ?? null,
      tail_width: data.tail_width ?? null,
      is_leasable: data.is_leasable,
      status: data.status,
      pricing_override: data.pricing_override,
      unit_monthly_rate: data.pricing_override ? (data.unit_monthly_rate ?? null) : null,
      unit_rate_per_sqft: data.pricing_override ? (data.unit_rate_per_sqft ?? null) : null,
    })
  }

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete unit ${unit.unit_code}?`)) {
      onDelete(unit.id)
    }
  }

  return (
    <div className="border rounded-lg p-4 bg-background space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold">Edit Unit: {unit.unit_code}</h4>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ×
        </Button>
      </div>

      <Form {...form}>
        <div className="space-y-4">
          <FormField
            control={form.control as any}
            name="unit_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit Code</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control as any}
              name="door_width"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Door Width</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder={String(template.door_width)}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Template: {template.door_width}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control as any}
              name="depth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Depth</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder={String(template.depth)}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Template: {template.depth}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control as any}
              name="tail_width"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tail Width</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder={String(template.tail_width)}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Template: {template.tail_width}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control as any}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {HANGAR_UNIT_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {HANGAR_UNIT_STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control as any}
            name="is_leasable"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Leasable</FormLabel>
                  <FormDescription>
                    Can this unit be leased to customers?
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Pricing Override Section */}
          <div className="space-y-3 pt-2 border-t">
            <FormField
              control={form.control as any}
              name="pricing_override"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Custom Pricing</FormLabel>
                    <FormDescription>
                      Override parent hangar pricing for this unit
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {watchedPricingOverride ? (
              // Show unit-specific pricing fields based on parent's pricing model
              parentPricingModel === 'fixed' ? (
                <FormField
                  control={form.control as any}
                  name="unit_monthly_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Rate ($)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={parentMonthlyRate ? String(parentMonthlyRate) : 'Enter rate'}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Parent rate: {parentMonthlyRate ? `$${parentMonthlyRate.toLocaleString()}` : 'Not set'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control as any}
                  name="unit_rate_per_sqft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rate per Sq Ft ($)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={parentRatePerSqft ? String(parentRatePerSqft) : 'Enter rate'}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Parent rate: {parentRatePerSqft ? `$${parentRatePerSqft}/sq ft` : 'Not set'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )
            ) : (
              // Show inherited pricing indicator
              <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                <span className="font-medium">Pricing:</span>{' '}
                {parentPricingModel === 'fixed' ? (
                  <>Inherited from hangar ({parentMonthlyRate ? `$${parentMonthlyRate.toLocaleString()}/mo` : 'Not set'})</>
                ) : (
                  <>Inherited from hangar ({parentRatePerSqft ? `$${parentRatePerSqft}/sq ft` : 'Not set'})</>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between pt-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleDelete}
            >
              Delete Unit
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isLoading}
                onClick={form.handleSubmit(handleSubmit) as any}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  )
}
